import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { startPayFastCheckout } from "@/lib/payfast";
import { useAuth } from "@/context/AuthContext";
import { Reveal, EASE_LUXE } from "@/components/Reveal";
import VoucherInput from "@/components/VoucherInput";

const FREE = {
  id: "free",
  name: "Free",
  amount: 0,
  perks: [
    { t: "Browse the full directory", ok: true },
    { t: "View up to 3 photos per profile", ok: true },
    { t: "Save favourite profiles", ok: false },
    { t: "Message companions", ok: false },
    { t: "Write & read reviews", ok: false },
  ],
};

const PREMIUM_PERKS = [
  { t: "Browse the full directory", ok: true },
  { t: "Unlimited photos on every profile", ok: true },
  { t: "Save unlimited favourites", ok: true },
  { t: "Message any companion directly", ok: true },
  { t: "Write & read all reviews", ok: true },
  { t: "Priority concierge support", ok: true },
];

export default function Membership() {
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();
  const [premiumPkg, setPremiumPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [voucher, setVoucher] = useState(null);

  useEffect(() => {
    api.get("/membership/packages").then((r) => setPremiumPkg(r.data.find((p) => p.id === "premium"))).catch(() => {});
  }, []);

  const subscribe = async () => {
    if (!user) { navigate("/register"); return; }
    setLoading(true);
    try {
      await startPayFastCheckout({
        package_id: "premium",
        voucher_code: voucher?.code || null,
      });
      // On success, PayFast redirects the browser away — no code runs after.
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const Perk = ({ ok, children, dark }) => (
    <li className={`flex items-start gap-3 text-sm font-light ${dark ? (ok ? "text-black/80" : "text-black/40") : (ok ? "text-white/75" : "text-white/35")}`}>
      {ok ? <Check size={16} className={`mt-0.5 shrink-0 ${dark ? "text-black" : "text-velvet-gold"}`} />
          : <X size={16} className="mt-0.5 shrink-0 opacity-50" />}
      <span className={ok ? "" : "line-through"}>{children}</span>
    </li>
  );

  return (
    <div className="pt-28 md:pt-40" data-testid="membership-page">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal>
            <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Membership</p>
            <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white md:text-7xl">
              Two ways to <span className="italic text-velvet-rose">play</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base font-light leading-relaxed text-white/50">
              Start free and browse. Go Premium to message, save and review — everything unlocked.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {/* FREE */}
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE_LUXE }} data-testid="tier-free" className="flex flex-col bg-velvet-ink p-10">
            <h3 className="font-heading text-3xl font-semibold text-white">Free</h3>
            <div className="mt-6 flex items-end gap-2">
              <span className="font-heading text-6xl font-bold text-white">R0</span>
              <span className="mb-2 text-xs uppercase tracking-luxe text-white/40">forever</span>
            </div>
            <ul className="mt-10 flex-1 space-y-4">
              {FREE.perks.map((p) => <Perk key={p.t} ok={p.ok}>{p.t}</Perk>)}
            </ul>
            <button
              onClick={() => (user ? toast.success("You're on the Free plan.") : navigate("/register"))}
              data-testid="tier-free-cta"
              className="mt-10 rounded-full border border-white/20 py-4 text-xs uppercase tracking-luxe text-white transition-colors hover:bg-white hover:text-black">
              {user ? (isPremium ? "Included" : "Your current plan") : "Get started free"}
            </button>
          </motion.div>

          {/* PREMIUM */}
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE_LUXE, delay: 0.1 }} data-testid="tier-premium" className="flex flex-col bg-velvet-rose p-10">
            <span className="mb-6 w-fit bg-black px-3 py-1 text-[10px] uppercase tracking-luxe text-velvet-gold">Gold &middot; Full access</span>
            <h3 className="font-heading text-3xl font-semibold text-black">Premium</h3>
            <div className="mt-6 flex items-end gap-2">
              <span className="font-heading text-6xl font-bold text-black">
                R{voucher
                  ? Number(voucher.final_amount).toLocaleString()
                  : (premiumPkg ? Number(premiumPkg.amount).toLocaleString() : "350")}
              </span>
              <span className="mb-2 text-xs uppercase tracking-luxe text-black/60">/ month</span>
              {voucher && (
                <span className="mb-2 ml-2 text-sm text-black/50 line-through">
                  R{Number(premiumPkg?.amount || 350).toLocaleString()}
                </span>
              )}
            </div>
            <ul className="mt-10 flex-1 space-y-4">
              {PREMIUM_PERKS.map((p) => <Perk key={p.t} ok={p.ok} dark>{p.t}</Perk>)}
            </ul>
            <button
              onClick={subscribe} disabled={loading || isPremium}
              data-testid="subscribe-premium"
              className="mt-10 rounded-full bg-black py-4 text-xs uppercase tracking-luxe text-white transition-colors hover:bg-velvet-ink disabled:opacity-60">
              {isPremium ? "You're Premium ✓" : loading ? "Redirecting…" : "Go Premium"}
            </button>
            {!isPremium && (
              <div className="mt-6 border-t border-black/10 pt-6">
                <div className="voucher-on-pink">
                  <VoucherInput
                    packageId="premium"
                    originalAmount={premiumPkg?.amount || 350}
                    onApplied={setVoucher}
                  />
                </div>
              </div>
            )}
          </motion.div>
        </div>
        <p className="mt-8 text-center text-xs text-white/30">Secure ZAR payments via PayFast Sandbox.</p>
      </div>
    </div>
  );
}
